
import React, { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield } from "lucide-react";
import { UserProfile } from "@/types/auth";
import { Card, CardContent } from "@/components/ui/card";

interface UserInfoCardProps {
  user: UserProfile | null;
  isLoading: boolean;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
}

const UserInfoCard: React.FC<UserInfoCardProps> = ({
  user,
  isLoading,
  updateProfile,
}) => {
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [isEditing, setIsEditing] = useState(false);

  // Update local state when user profile changes
  React.useEffect(() => {
    if (user) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
    }
  }, [user]);

  const handleUpdateProfile = async () => {
    await updateProfile({
      first_name: firstName,
      last_name: lastName,
    });
    setIsEditing(false);
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "U";

    let initials = "";
    if (firstName) initials += firstName[0].toUpperCase();
    if (lastName) initials += lastName[0].toUpperCase();
    return initials;
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center">
          <Avatar className="w-16 h-16">
            <AvatarImage src={user?.avatar_url || ""} alt={user?.first_name || "User"} />
            <AvatarFallback className="text-xl">
              {getInitials(user?.first_name, user?.last_name)}
            </AvatarFallback>
          </Avatar>
          <div className="ml-4">
            <h2 className="text-xl font-semibold">
              {user?.first_name ? `${user.first_name} ${user.last_name || ""}` : "User"}
            </h2>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            {user?.beta_access && (
              <Badge variant="outline" className="bg-primary/10 mt-1">
                <Shield className="w-3 h-3 mr-1" />
                Beta Access
              </Badge>
            )}
          </div>
        </div>

        {!isEditing ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </Button>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleUpdateProfile} disabled={isLoading}>
                {isLoading ? "Saving..." : "Save"}
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserInfoCard;
